import type { ScoreRequest, ScoreResult, ScorerLoadProgress, SpeechScorer } from '../types';
import type { FromWorker, ToWorker } from './protocol';

import models from './models.json';

/**
 * Where the models come from: this site's public/models in development and tests, or the pinned
 * hub URLs in deployed builds (`VITE_MODELS=hub`; static hosts limit file sizes), verified by hash.
 */
const FROM_HUB = import.meta.env.VITE_MODELS === 'hub';
const { model: MODEL, secondModel: SECOND, tokens: TOKENS } = models.files;
const source = (file: { path: string; url: string }) =>
  FROM_HUB ? file.url : `${import.meta.env.BASE_URL}models/${file.path}`;
/**
 * One WebAssembly thread: a word scores in well under 100 ms on one core, so threads would add
 * little, while costing CPU on phones and needing cross-origin isolation and pthread workers that
 * bundlers handle poorly (they hung under the Vite dev server).
 */
const SCORER_THREADS = 1;

interface Pending {
  resolve: (result: ScoreResult) => void;
  reject: (error: Error) => void;
}

/** The app's scorer: the ZIPA phone recognizer and /ɹ/ classifier in a Web Worker. */
export class WorkerScorer implements SpeechScorer {
  private worker: Worker | undefined;
  private disposed = false;
  private nextId = 1;
  private readonly pending = new Map<number, Pending>();
  private loading: Promise<void> | undefined;
  private loadSettled: { resolve: () => void; reject: (error: Error) => void } | undefined;
  private onProgress: ScorerLoadProgress | undefined;
  /** Milliseconds of the latest inference, for diagnostics. */
  lastInferenceMs = 0;
  /** WebAssembly threads in use once loaded. */
  threads = 0;

  /** Starts the worker on first use, so creating a scorer costs nothing until it loads. */
  private ensureWorker(): Worker {
    if (this.disposed) throw new Error('speech scorer disposed');
    if (!this.worker) {
      const worker = new Worker(new URL('./scorer.worker.ts', import.meta.url), {
        type: 'module',
        name: 'speech-scorer',
      });
      worker.onmessage = (event: MessageEvent<FromWorker>) => {
        this.handle(event.data);
      };
      worker.onerror = (event) => {
        this.failAll(new Error(event.message || 'speech scorer worker failed'));
      };
      this.worker = worker;
    }
    return this.worker;
  }

  load(onProgress?: ScorerLoadProgress): Promise<void> {
    this.onProgress = onProgress;
    this.loading ??= new Promise<void>((resolve, reject) => {
      this.loadSettled = { resolve, reject };
      this.post({
        type: 'load',
        modelUrl: source(MODEL),
        secondModelUrl: source(SECOND),
        tokensUrl: source(TOKENS),
        ...(FROM_HUB
          ? { sha256: { model: MODEL.sha256, secondModel: SECOND.sha256, tokens: TOKENS.sha256 } }
          : {}),
        threads: SCORER_THREADS,
      });
    });
    return this.loading;
  }

  score(request: ScoreRequest): Promise<ScoreResult> {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.post({ type: 'score', id, request });
    });
  }

  dispose(): void {
    this.disposed = true;
    this.worker?.terminate();
    this.worker = undefined;
    this.failAll(new Error('speech scorer disposed'));
  }

  private post(message: ToWorker) {
    this.ensureWorker().postMessage(message);
  }

  private handle(message: FromWorker) {
    switch (message.type) {
      case 'progress':
        this.onProgress?.(message.loaded, message.total);
        break;
      case 'ready':
        this.threads = message.threads;
        this.loadSettled?.resolve();
        break;
      case 'result':
        this.lastInferenceMs = message.inferenceMs;
        this.pending.get(message.id)?.resolve(message.result);
        this.pending.delete(message.id);
        break;
      case 'error': {
        const error = new Error(message.message);
        if (message.id === undefined) {
          this.loading = undefined;
          this.loadSettled?.reject(error);
        } else {
          this.pending.get(message.id)?.reject(error);
          this.pending.delete(message.id);
        }
        break;
      }
    }
  }

  private failAll(error: Error) {
    this.loadSettled?.reject(error);
    for (const { reject } of this.pending.values()) reject(error);
    this.pending.clear();
  }
}
